/**
 * /admin/course-fit/governance — Fast Match release governance view.
 *
 * Adds the smallest coherent governance surface inside the existing
 * WUA admin (see AdminHome.jsx). Reuses:
 *   - the existing Google-OAuth + ADMIN_EMAILS allow-list via
 *     useAuth() + /api/admin/whoami;
 *   - the same three-state UX (loading -> signin -> forbidden -> content)
 *     as every sibling admin page;
 *   - the same Card/Button/table primitives as AdminReviews / AdminVerify.
 *
 * No new role system, no new global chrome, no BI library, no arbitrary
 * editor. Every mutation lands on the backend admin API which enforces
 * governance rules (immutability, only-one-active partial index,
 * validation gate, golden-fixture gate, feature-flag audit).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import CmsSwitcherBar from "@/components/app/CmsSwitcherBar";
import {
  ShieldCheck, ShieldAlert, Loader2, ArrowRight,
  CheckCircle2, XCircle, AlertTriangle, Download, Copy,
} from "lucide-react";


const INTEGRITY_ORDER = [
  ["families",         "Families"],
  ["subjects",         "Subjects"],
  ["anchors",          "Anchors"],
  ["dimensions",       "Dimensions"],
  ["scores",           "Scores"],
  ["evidence",         "Evidence"],
  ["questions",        "Questions"],
  ["mappings",         "Mappings"],
  ["signatures",       "Signatures"],
  ["golden_fixtures",  "Golden fixtures"],
];


function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toISOString().replace("T", " ").slice(0, 19) + " UTC"; }
  catch { return String(v); }
}


function Pill({ ok, children, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-underground-sc tracking-wider ${
        ok
          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
          : "border-red-300 bg-red-50 text-red-800"
      }`}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {children}
    </span>
  );
}


export default function AdminCourseFitGovernance() {
  const { user, loading, login } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [status, setStatus] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [audit, setAudit] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [busy, setBusy] = useState(null); // "validate" | "activate" | "retire" | "flag"
  const [confirm, setConfirm] = useState(null); // { kind, releaseId }
  const [notice, setNotice] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);
  const [reportJson, setReportJson] = useState(null);

  const loadAll = useCallback(async () => {
    setErrorMsg(null);
    try {
      const [s, i, m, a] = await Promise.all([
        api.get("/admin/course-fit/status"),
        api.get("/admin/course-fit/integrity"),
        api.get(`/admin/course-fit/metrics?since_hours=720&include_test=${includeTest}`),
        api.get("/admin/course-fit/audit?limit=25"),
      ]);
      setStatus(s.data);
      setIntegrity(i.data);
      setMetrics(m.data);
      setAudit(a.data.events || []);
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message || "Load failed");
    }
  }, [includeTest]);

  useEffect(() => {
    if (loading) return;
    if (!user) { setChecking(false); return; }
    (async () => {
      try {
        const r = await api.get("/admin/whoami");
        setIsAdmin(!!r.data?.is_admin);
        setChecking(false);
      } catch {
        setIsAdmin(false); setChecking(false);
      }
    })();
  }, [user, loading]);

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin, loadAll]);

  const runValidate = async () => {
    setBusy("validate"); setNotice(null); setErrorMsg(null);
    try {
      const r = await api.post("/admin/course-fit/release/validate");
      setNotice(
        r.data.activatable
          ? `Draft validated. Release ${r.data.release.releaseId} is activatable.`
          : `Draft validated. Not activatable: ${r.data.reason}.`
      );
      await loadAll();
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message || "Validate failed");
    } finally { setBusy(null); }
  };

  const runActivate = async (releaseId) => {
    setBusy("activate"); setNotice(null); setErrorMsg(null); setConfirm(null);
    try {
      await api.post(`/admin/course-fit/release/${encodeURIComponent(releaseId)}/activate`);
      setNotice(`Release ${releaseId} is now active.`);
      await loadAll();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErrorMsg(typeof d === "object" ? `${d.code}: ${d.message}` : (d || err.message));
    } finally { setBusy(null); }
  };

  const runRetire = async (releaseId) => {
    setBusy("retire"); setNotice(null); setErrorMsg(null); setConfirm(null);
    try {
      await api.post(`/admin/course-fit/release/${encodeURIComponent(releaseId)}/retire`);
      setNotice(`Release ${releaseId} retired.`);
      await loadAll();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setErrorMsg(typeof d === "object" ? `${d.code}: ${d.message}` : (d || err.message));
    } finally { setBusy(null); }
  };

  const runFlagSet = async (enabled) => {
    setBusy("flag"); setNotice(null); setErrorMsg(null);
    try {
      const r = await api.post("/admin/course-fit/flag", { enabled });
      const eff = r.data.effective ? "on" : "off";
      setNotice(`Flag saved (DB=${enabled}). Effective state: ${eff}.` +
        (r.data.envAllows ? "" : " Env master switch is off — effective remains off."));
      await loadAll();
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message || "Flag save failed");
    } finally { setBusy(null); }
  };

  const openReport = async (releaseId) => {
    setErrorMsg(null);
    try {
      const r = await api.get(`/admin/course-fit/release/${encodeURIComponent(releaseId)}/report`);
      setReportJson(JSON.stringify(r.data, null, 2));
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message || "Report unavailable");
    }
  };

  const downloadReport = () => {
    if (!reportJson) return;
    const blob = new Blob([reportJson], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "course-fit-release-report.json";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyReport = async () => {
    if (!reportJson) return;
    try { await navigator.clipboard.writeText(reportJson);
          setNotice("Report copied to clipboard."); }
    catch { setErrorMsg("Clipboard blocked; use Download JSON instead."); }
  };

  /* --------------- loading / signin / forbidden --------------- */
  if (loading || checking) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm"
           data-testid="course-fit-gov-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading governance…
      </div>
    );
  }
  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24" data-testid="course-fit-gov-signin">
        <div className="p-10 border border-stone-300 rounded-xl bg-white">
          <ShieldCheck className="h-4 w-4" />
          <h1 className="mt-3 font-editorial text-3xl">Sign in to view governance</h1>
          <p className="mt-2 text-stone-600 text-sm">Access is restricted to the admin allowlist.</p>
          <Button className="mt-6" data-testid="course-fit-gov-signin-btn" onClick={login}>
            Sign in with Google <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="course-fit-gov-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Not on the editorial allowlist</h1>
          <p className="mt-2 text-stone-600 text-sm">
            Signed in as <span className="font-mono">{user.email}</span>. Adding
            a colleague to <span className="font-mono">ADMIN_EMAILS</span> grants
            release-governance authority.
          </p>
          <div className="mt-4">
            <Link to="/admin"><Button variant="outline" size="sm">Back to CMS</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  /* --------------- content --------------- */
  const flag = status?.flag;
  const active = status?.activeRelease;
  const configuredId = status?.configuredReleaseId;
  const configuredRow = (status?.releases || []).find(r => r.releaseId === configuredId);

  return (
    <>
      <CmsSwitcherBar />
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="course-fit-gov-page">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
              Fast Match / Governance
            </div>
            <h1 className="mt-1 font-editorial text-4xl">Release status &amp; beta health</h1>
            <p className="mt-2 text-stone-600 text-sm">
              Signed in as <span className="font-mono">{user.email}</span>. Active releases are
              immutable; wording, mapping, weight, or profile changes require a new versioned
              release.
            </p>
          </div>
          <Link to="/admin"><Button variant="outline" size="sm">Back to CMS</Button></Link>
        </div>

        {errorMsg && (
          <div role="alert" data-testid="course-fit-gov-error"
               className="mt-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="h-4 w-4 inline mr-1" />
            {typeof errorMsg === "string" ? errorMsg : JSON.stringify(errorMsg)}
          </div>
        )}
        {notice && (
          <div role="status" data-testid="course-fit-gov-notice"
               className="mt-6 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            {notice}
          </div>
        )}

        {/* --- Release status --- */}
        <section className="mt-8 border border-stone-300 rounded-lg bg-white p-6"
                 data-testid="course-fit-gov-status">
          <h2 className="font-editorial text-2xl">Release</h2>
          <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-[11px] font-underground-sc tracking-wider text-stone-500">Configured (on-disk)</dt>
              <dd data-testid="course-fit-gov-configured-id" className="mt-1 font-mono">
                {configuredId || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-underground-sc tracking-wider text-stone-500">Active</dt>
              <dd data-testid="course-fit-gov-active-id" className="mt-1 font-mono">
                {active ? active.releaseId : <span className="text-stone-500">no active release</span>}
              </dd>
              {active && (
                <div className="mt-1 text-[11px] text-stone-500">
                  activated {fmtDate(active.activatedAt)} by {active.activatedBy || "—"}
                </div>
              )}
            </div>
          </dl>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" data-testid="course-fit-gov-validate-btn"
                    onClick={runValidate} disabled={busy === "validate"}>
              {busy === "validate" ? "Validating…" : "Validate draft"}
            </Button>
            {configuredRow?.status === "draft" && (
              <Button size="sm" data-testid="course-fit-gov-activate-btn"
                      onClick={() => setConfirm({ kind: "activate", releaseId: configuredId })}>
                Activate…
              </Button>
            )}
            {active && (
              <Button size="sm" variant="outline"
                      data-testid="course-fit-gov-retire-btn"
                      onClick={() => setConfirm({ kind: "retire", releaseId: active.releaseId })}>
                Retire active…
              </Button>
            )}
            {configuredRow && (
              <Button size="sm" variant="outline"
                      data-testid="course-fit-gov-report-btn"
                      onClick={() => openReport(configuredId)}>
                View validation report
              </Button>
            )}
          </div>
        </section>

        {/* --- Integrity — 10 canonical categories --- */}
        <section className="mt-6 border border-stone-300 rounded-lg bg-white p-6"
                 data-testid="course-fit-gov-integrity">
          <h2 className="font-editorial text-2xl">Integrity</h2>
          {!integrity && <p className="mt-3 text-sm text-stone-500">Loading…</p>}
          {integrity && (
            <>
              <table className="mt-4 w-full text-sm" data-testid="course-fit-gov-integrity-table">
                <thead>
                  <tr className="text-left text-[11px] font-underground-sc tracking-wider text-stone-500 border-b">
                    <th className="pb-2">Category</th>
                    <th className="pb-2">Count</th>
                    <th className="pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {INTEGRITY_ORDER.map(([key, label]) => {
                    const c = (integrity.categories || []).find(x => x.key === key);
                    if (!c) return null;
                    const isGolden = key === "golden_fixtures";
                    return (
                      <tr key={key}
                          data-testid={`course-fit-gov-integrity-row-${key}`}
                          className="border-b last:border-b-0">
                        <td className="py-2 font-mono">{label}</td>
                        <td className="py-2 font-mono">
                          {c.count == null ? "—" :
                            isGolden ? `${c.count}/${c.total}` : c.count}
                        </td>
                        <td className="py-2">
                          <Pill ok={!!c.ok}
                                testid={`course-fit-gov-integrity-status-${key}`}>
                            {c.ok ? "ok" : "fail"}
                          </Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {integrity.errors?.length ? (
                <div className="mt-3 text-xs text-red-700">
                  {integrity.errors.length} data errors — see report for details.
                </div>
              ) : null}
            </>
          )}
        </section>

        {/* --- Feature flag --- */}
        <section className="mt-6 border border-stone-300 rounded-lg bg-white p-6"
                 data-testid="course-fit-gov-flag">
          <h2 className="font-editorial text-2xl">Beta feature flag</h2>
          {!flag && <p className="mt-3 text-sm text-stone-500">Loading…</p>}
          {flag && (
            <>
              <dl className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <dt className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                    Env master
                  </dt>
                  <dd data-testid="course-fit-gov-flag-env" className="mt-1">
                    <Pill ok={flag.envAllows}>{flag.envAllows ? "permits beta" : "master off"}</Pill>
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                    Database setting
                  </dt>
                  <dd data-testid="course-fit-gov-flag-db" className="mt-1 font-mono">
                    {flag.dbSetting == null ? "unset" : (flag.dbSetting ? "on" : "off")}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                    Effective
                  </dt>
                  <dd data-testid="course-fit-gov-flag-effective" className="mt-1">
                    <Pill ok={flag.effective}>{flag.effective ? "beta on" : "beta off"}</Pill>
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-xs text-stone-500">
                The env variable is the emergency master switch and is edited outside
                the app. When the env permits the beta, the audited database toggle
                below controls on/off; otherwise the effective state remains off.
              </p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" data-testid="course-fit-gov-flag-on-btn"
                        onClick={() => runFlagSet(true)}
                        disabled={busy === "flag" || flag.dbSetting === true}>
                  Turn beta on
                </Button>
                <Button size="sm" variant="outline"
                        data-testid="course-fit-gov-flag-off-btn"
                        onClick={() => runFlagSet(false)}
                        disabled={busy === "flag" || flag.dbSetting === false}>
                  Turn beta off
                </Button>
              </div>
              {flag.updatedAt && (
                <p className="mt-3 text-[11px] font-mono text-stone-500">
                  Last change: {fmtDate(flag.updatedAt)} by {flag.updatedBy || "—"}
                </p>
              )}
            </>
          )}
        </section>

        {/* --- Metrics --- */}
        <section className="mt-6 border border-stone-300 rounded-lg bg-white p-6"
                 data-testid="course-fit-gov-metrics">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <h2 className="font-editorial text-2xl">Operational metrics</h2>
            <label className="text-xs text-stone-600 flex items-center gap-2">
              <input type="checkbox" checked={includeTest}
                     data-testid="course-fit-gov-include-test"
                     onChange={(e) => setIncludeTest(e.target.checked)} />
              Include internal/test sessions
            </label>
          </div>
          {!metrics && <p className="mt-3 text-sm text-stone-500">Loading…</p>}
          {metrics && (
            <>
              <p className="mt-1 text-[11px] font-mono text-stone-500 uppercase tracking-wider">
                Window: last {metrics.sinceHours}h · include_test={String(metrics.includeTest)}
              </p>
              <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
                <Stat label="Starts" value={metrics.operational.starts}
                      testid="metric-starts" />
                <Stat label="Completions" value={metrics.operational.completions}
                      testid="metric-completions" />
                <Stat label="Completion rate"
                      value={`${(100 * (metrics.operational.completionRate || 0)).toFixed(1)}%`}
                      testid="metric-completion-rate" />
                <Stat label="Duration p50"
                      value={metrics.operational.durationSummarySeconds?.p50 != null
                        ? `${metrics.operational.durationSummarySeconds.p50}s` : "—"}
                      testid="metric-duration-p50" />
                <Stat label="Feedback avg"
                      value={metrics.productLearning.feedbackRatingAverage ?? "—"}
                      testid="metric-feedback-avg" />
                <Stat label="Close-pair rate"
                      value={`${(100 * (metrics.productLearning.closePairRate || 0)).toFixed(1)}%`}
                      testid="metric-close-pair" />
                <Stat label="Low-coverage rate"
                      value={`${(100 * (metrics.productLearning.lowCoverageRate || 0)).toFixed(1)}%`}
                      testid="metric-low-coverage" />
                <Stat label="Both/Neither rate"
                      value={`${(100 * (metrics.productLearning.bothNeitherRate || 0)).toFixed(1)}%`}
                      testid="metric-both-neither" />
              </div>
              {(!metrics.operational.starts && !metrics.operational.completions) && (
                <p className="mt-3 text-xs text-stone-500" data-testid="course-fit-gov-metrics-empty">
                  No sessions yet in this window.
                </p>
              )}
            </>
          )}
        </section>

        {/* --- Audit --- */}
        <section className="mt-6 border border-stone-300 rounded-lg bg-white p-6"
                 data-testid="course-fit-gov-audit">
          <h2 className="font-editorial text-2xl">Recent governance events</h2>
          {!audit.length && (
            <p className="mt-3 text-sm text-stone-500" data-testid="course-fit-gov-audit-empty">
              No events recorded.
            </p>
          )}
          {audit.length > 0 && (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-underground-sc tracking-wider text-stone-500 border-b">
                  <th className="pb-2">When</th>
                  <th className="pb-2">Action</th>
                  <th className="pb-2">Actor</th>
                  <th className="pb-2">Payload</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e, i) => (
                  <tr key={i} data-testid={`course-fit-gov-audit-row-${i}`}
                      className="border-b last:border-b-0">
                    <td className="py-2 font-mono text-[11px]">{fmtDate(e.ts)}</td>
                    <td className="py-2 font-mono">{e.action}</td>
                    <td className="py-2">{e.actorEmail || "—"}</td>
                    <td className="py-2 font-mono text-[11px]">{JSON.stringify(e.payload)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* --- Confirmation modal --- */}
        {confirm && (
          <div role="dialog" aria-modal="true"
               data-testid="course-fit-gov-confirm"
               className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg border border-stone-300 p-6 max-w-md w-full">
              <h3 className="font-editorial text-xl">
                {confirm.kind === "activate" ? "Activate release" : "Retire release"}
              </h3>
              <p className="mt-2 text-sm text-stone-700">
                {confirm.kind === "activate"
                  ? "This will retire the current active release (if any) and activate this one atomically."
                  : "This will retire the active release. Retired releases cannot be reactivated."}
              </p>
              <p className="mt-2 text-sm font-mono">{confirm.releaseId}</p>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" size="sm"
                        data-testid="course-fit-gov-confirm-cancel"
                        onClick={() => setConfirm(null)}>Cancel</Button>
                <Button size="sm"
                        data-testid="course-fit-gov-confirm-yes"
                        onClick={() => (confirm.kind === "activate"
                          ? runActivate(confirm.releaseId)
                          : runRetire(confirm.releaseId))}
                        disabled={busy != null}>
                  {busy ? "Working…" : "Confirm"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* --- Report drawer --- */}
        {reportJson && (
          <div role="dialog" aria-modal="true"
               data-testid="course-fit-gov-report-drawer"
               className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg border border-stone-300 p-6 max-w-3xl w-full max-h-[80vh] flex flex-col">
              <div className="flex items-baseline justify-between">
                <h3 className="font-editorial text-xl">Release validation report</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                          data-testid="course-fit-gov-report-copy"
                          onClick={copyReport}>
                    <Copy className="h-3 w-3 mr-1" /> Copy
                  </Button>
                  <Button size="sm" variant="outline"
                          data-testid="course-fit-gov-report-download"
                          onClick={downloadReport}>
                    <Download className="h-3 w-3 mr-1" /> Download JSON
                  </Button>
                  <Button size="sm"
                          data-testid="course-fit-gov-report-close"
                          onClick={() => setReportJson(null)}>Close</Button>
                </div>
              </div>
              <pre className="mt-4 flex-1 overflow-auto text-xs font-mono bg-stone-50 p-3 rounded border">
                {reportJson}
              </pre>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


function Stat({ label, value, testid }) {
  return (
    <div className="border border-stone-200 rounded p-3">
      <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">{label}</div>
      <div data-testid={testid} className="mt-1 font-editorial text-2xl">{value}</div>
    </div>
  );
}
